import { Plus, Camera, ClipboardPaste } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CATEGORIES, CATEGORY_LABELS, PANTRY_TO_SHOPPING } from "@/lib/shoppingConstants";

const UNITS = ["stk", "kg", "g", "l", "dl", "ss", "ts", "pk", "boks", "pose", "fl"];

/**
 * QuickAddBar — sticky bottom bar for adding items to the shopping list.
 * Primary action is the large amber Enter-to-submit textarea. Optional details
 * (mengde, unit, category) hide behind a collapse toggle.
 */
export default function QuickAddBar({
  // primary input
  newItemName,
  setNewItemName,
  // suggestions popup
  suggestionsOpen,
  setSuggestionsOpen,
  nameSuggestions,
  suggestLoading,
  onSelectSuggestion,
  // details (optional)
  showAddDetails,
  setShowAddDetails,
  newItemAmount,
  setNewItemAmount,
  newItemUnit,
  setNewItemUnit,
  newItemCategory,
  setNewItemCategory,
  // actions
  onQuickAddSubmit,
  onAddManualItem,
  onOpenBarcode,
  onOpenNotes,
}) {
  return (
    <div className="sticky bottom-0 mt-6 pb-4">
      <div className="bg-white border border-stone-200 rounded-xl p-4 shadow-lg shadow-stone-200/50 space-y-3" data-testid="add-item-form">
        {/* Big quick-add row — primary input on mobile + desktop */}
        <div className="flex gap-2" data-testid="quick-add-row">
          <textarea
            value={newItemName}
            onChange={(e) => { setNewItemName(e.target.value); setSuggestionsOpen(true); }}
            onFocus={() => setSuggestionsOpen(true)}
            onBlur={() => setTimeout(() => setSuggestionsOpen(false), 150)}
            onKeyDown={(e) => {
              // Enter alone submits, Shift+Enter inserts newline (for multi-line)
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onQuickAddSubmit();
              }
            }}
            placeholder="Skriv vare og trykk Enter… (Shift+Enter eller komma for flere)"
            rows={1}
            className="flex-1 min-w-0 rounded-lg border-2 border-amber-300 bg-amber-50/40 px-4 py-4 text-lg font-medium placeholder:text-amber-700/50 focus:border-amber-500 focus:bg-white focus:ring-2 focus:ring-amber-200 outline-none transition-all resize-none"
            data-testid="quick-add-input"
            autoComplete="off"
            style={{ minHeight: "60px", maxHeight: "200px" }}
          />
          <button
            onClick={onOpenBarcode}
            className="inline-flex items-center justify-center rounded-lg border border-stone-200 bg-white px-3 py-4 text-stone-600 hover:bg-stone-50 transition-colors flex-shrink-0"
            data-testid="shopping-barcode-btn"
            title="Skann strekkode for å finne og legge til vare"
            type="button"
          >
            <Camera className="h-5 w-5" />
          </button>
          <button
            onClick={onQuickAddSubmit}
            disabled={!newItemName.trim()}
            className="inline-flex items-center justify-center rounded-lg bg-amber-600 px-5 py-4 text-base font-semibold text-white hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors gap-2 flex-shrink-0"
            data-testid="quick-add-btn"
            title="Legg til (henter pris automatisk)"
          >
            <Plus className="h-5 w-5" /> <span className="hidden sm:inline">Legg til</span>
          </button>
        </div>

        <div className="flex items-center justify-between">
          <button
            onClick={() => setShowAddDetails(!showAddDetails)}
            className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-stone-400 font-semibold hover:text-stone-600"
            data-testid="toggle-add-details"
          >
            {showAddDetails ? "▾" : "▸"} Detaljer (valgfritt)
          </button>
          <button onClick={onOpenNotes} className="inline-flex items-center gap-1.5 text-xs text-navy-800 hover:text-navy-800 font-medium transition-colors" data-testid="import-notes-btn">
            <ClipboardPaste className="h-3.5 w-3.5" /> Importer fra notater
          </button>
        </div>
        {showAddDetails && (
        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              value={newItemName}
              onChange={(e) => { setNewItemName(e.target.value); setSuggestionsOpen(true); }}
              onFocus={() => setSuggestionsOpen(true)}
              onBlur={() => setTimeout(() => setSuggestionsOpen(false), 150)}
              placeholder="Varenavn *"
              className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none transition-colors"
              data-testid="manual-item-input"
              autoComplete="off"
              onKeyDown={(e) => e.key === "Enter" && onAddManualItem()}
            />
            {suggestionsOpen && nameSuggestions.length > 0 && (
              <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-stone-200 rounded-md shadow-lg max-h-64 overflow-y-auto" data-testid="shopping-name-suggestions">
                <p className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-stone-400 font-semibold border-b border-stone-100">
                  Tidligere varer {suggestLoading && <span className="opacity-60">...</span>}
                </p>
                {nameSuggestions.map((s) => {
                  const shoppingCat = PANTRY_TO_SHOPPING[s.category] || s.category;
                  return (
                    <button
                      type="button"
                      key={s.name}
                      onMouseDown={(e) => { e.preventDefault(); onSelectSuggestion(s); }}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-sailcloth-100 text-left border-b border-stone-50 last:border-0"
                      data-testid={`shopping-name-suggestion-${s.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <span className="font-medium text-stone-800 truncate">{s.name}</span>
                      <span className="flex items-center gap-1.5 flex-shrink-0">
                        {shoppingCat && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-600">
                            {CATEGORY_LABELS[shoppingCat] || shoppingCat}
                          </span>
                        )}
                        {s.count > 0 && <span className="text-[10px] text-stone-400">×{s.count}</span>}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <input type="number" step="0.1" value={newItemAmount} onChange={(e) => setNewItemAmount(e.target.value)} placeholder="Mengde" className="w-20 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none transition-colors" data-testid="manual-item-amount" />
          <Select value={newItemUnit} onValueChange={setNewItemUnit}>
            <SelectTrigger className="w-24 border-stone-200" data-testid="manual-item-unit"><SelectValue /></SelectTrigger>
            <SelectContent>
              {UNITS.map((u) => (<SelectItem key={u} value={u}>{u}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={newItemCategory} onValueChange={setNewItemCategory}>
            <SelectTrigger className="w-32 border-stone-200" data-testid="manual-item-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{CATEGORY_LABELS[c] || c}</SelectItem>))}
            </SelectContent>
          </Select>
          <button onClick={onAddManualItem} className="inline-flex items-center justify-center rounded-md bg-navy-800 px-4 py-2 text-sm font-medium text-white hover:bg-navy-900 transition-colors gap-2" data-testid="add-manual-item-btn">
            <Plus className="h-4 w-4" /> Legg til
          </button>
        </div>
        )}
      </div>
    </div>
  );
}
